import React, { useCallback, useEffect, useState } from "react";
import { NavLink, Route, Routes, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/auth/AuthContext";
import AdminWorkspace from "@/admin/AdminWorkspace";
import "@/admin/AdminApp.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_OPTIONS = [
  "DEALER_OWNER", "STORE_ADMIN", "SALES", "DESIGNER", "CATALOG_MANAGER",
  "MANUFACTURER_ADMIN", "PRODUCTION_MANAGER", "FINANCE_MANAGER", "LOGISTICS_MANAGER",
  "INSTALLER", "CLIENT", "SUPERADMIN",
];

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(", ");
  return e?.message || "Something went wrong";
};

const initials = (name = "") =>
  name.split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "·";

/* ───────────────────────── small primitives ───────────────────────── */

function Field({ label, children }) {
  return (
    <label className="lhc-field">
      <span>{label}</span>
      {children}
    </label>
  );
}

function Modal({ title, hint, children, onClose }) {
  return (
    <div className="lhc-modal-overlay" onMouseDown={onClose}>
      <div className="lhc-modal" onMouseDown={(e) => e.stopPropagation()}>
        <h3>{title}</h3>
        {hint && <p className="hint">{hint}</p>}
        {children}
      </div>
    </div>
  );
}

function Switch({ checked, onChange, disabled }) {
  return (
    <label className="lhc-switch">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="track"><span className="knob" /></span>
    </label>
  );
}

function StatusBadge({ status }) {
  const s = status === "suspended" ? "suspended" : "active";
  return <span className={`lhc-badge ${s}`}><span className="lhc-dot" />{s === "suspended" ? "Suspended" : "Active"}</span>;
}

/* ───────────────────────── dashboard ───────────────────────── */

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    axios.get(`${API}/admin/stats`).then((r) => setStats(r.data)).catch((e) => setErr(errText(e)));
  }, []);
  if (err) return <div className="lhc-admin-msg err">{err}</div>;
  if (!stats) return <div className="lhc-loading">Loading platform metrics…</div>;
  const cards = [
    ["Total accounts", stats.total_users],
    ["Dealers", stats.total_dealers],
    ["Active dealers", stats.active_dealers],
    ["Stores", stats.total_stores],
    ["Projects", stats.total_projects],
    ["Configurations", stats.active_configurations],
    ["Orders (this month)", stats.orders_this_month],
    ["Products", stats.products],
    ["Feature flags on", stats.active_feature_flags],
    ["Audit events", stats.audit_events],
  ];
  const sys = stats.system || {};
  return (
    <>
      <div className="lhc-admin-head">
        <h1>Platform overview</h1>
        <p>Live metrics computed from MongoDB — no fabricated numbers.</p>
      </div>
      <div className="lhc-kpis">
        {cards.map(([label, num]) => (
          <div className="lhc-kpi" key={label} data-testid={`kpi-${label}`}>
            <div className="lhc-kpi-label">{label}</div>
            <div className="lhc-kpi-num">{num ?? 0}</div>
            <div className="lhc-kpi-sub">from live data</div>
          </div>
        ))}
      </div>
      <div className="lhc-panel">
        <div className="lhc-panel-head"><h2>System</h2></div>
        <table className="lhc-table">
          <tbody>
            <tr><td>Environment</td><td className="lhc-mono">{sys.environment}</td></tr>
            <tr><td>App version</td><td className="lhc-mono">{sys.app_version}</td></tr>
            <tr><td>Database</td><td className="lhc-mono">{sys.database}</td></tr>
            <tr><td>Region</td><td className="lhc-mono">{sys.region || "—"}</td></tr>
            <tr><td>Last backup</td><td className="lhc-mono">{sys.last_backup || "—"}</td></tr>
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ───────────────────────── tenants ───────────────────────── */

function TenantsPage() {
  const [tenants, setTenants] = useState(null);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", owner_email: "", owner_name: "", owner_password: "", city: "" });

  const load = useCallback(() => {
    axios.get(`${API}/admin/tenants`).then((r) => setTenants(r.data)).catch((e) => setErr(errText(e)));
  }, []);
  useEffect(load, [load]);

  const setStatus = async (t, status) => {
    setErr(""); setOk("");
    try {
      await axios.patch(`${API}/admin/tenants/${t.id}`, { status });
      setOk(`${t.name} → ${status}`);
      load();
    } catch (e) { setErr(errText(e)); }
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setOk("");
    try {
      const { data } = await axios.post(`${API}/admin/tenants`, form);
      setOk(`Dealer "${data.name}" created — owner ${data.owner_email}` +
        (data.owner_temp_password ? ` (temp password ${data.owner_temp_password})` : ""));
      setShowCreate(false);
      setForm({ name: "", owner_email: "", owner_name: "", owner_password: "", city: "" });
      load();
    } catch (e2) { setErr(errText(e2)); } finally { setBusy(false); }
  };

  return (
    <>
      <div className="lhc-admin-head">
        <h1>Tenants</h1>
        <p>Every store/dealer is one tenant. Suspending a dealer blocks all its users from signing in.</p>
      </div>
      {err && <div className="lhc-admin-msg err">{err}</div>}
      {ok && <div className="lhc-admin-msg ok">{ok}</div>}
      <div className="lhc-panel">
        <div className="lhc-panel-head">
          <div><h2>All tenants</h2><div className="sub">{tenants ? `${tenants.length} tenants` : ""}</div></div>
          <button className="lhc-btn primary" data-testid="new-tenant-btn" onClick={() => setShowCreate(true)}>+ New dealer</button>
        </div>
        {!tenants ? <div className="lhc-loading">Loading…</div> : (
          <table className="lhc-table">
            <thead><tr><th>Name</th><th>Type</th><th>Status</th><th>Users</th><th style={{ textAlign: "right" }}>Actions</th></tr></thead>
            <tbody>
              {tenants.map((t) => (
                <tr key={t.id} data-testid={`tenant-row-${t.id}`}>
                  <td style={{ fontWeight: 600, color: "#fff" }}>{t.name}</td>
                  <td><span className="lhc-badge type">{t.type}</span></td>
                  <td><StatusBadge status={t.status} /></td>
                  <td className="lhc-mono">{t.user_count ?? "—"}</td>
                  <td style={{ textAlign: "right" }}>
                    {t.type === "platform" ? <span style={{ color: "#6f6f78", fontSize: 12 }}>—</span>
                      : t.status === "suspended"
                        ? <button className="lhc-btn ghost sm" onClick={() => setStatus(t, "active")}>Activate</button>
                        : <button className="lhc-btn danger sm" onClick={() => setStatus(t, "suspended")}>Suspend</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <Modal title="New dealer tenant" hint="Provisions a dealer + store + an owner login. No invites." onClose={() => setShowCreate(false)}>
          <form onSubmit={create}>
            <Field label="Dealer name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Owner email"><input required type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} /></Field>
            <Field label="Owner name"><input value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} /></Field>
            <Field label="Owner password (blank → Welcome123!)"><input value={form.owner_password} onChange={(e) => setForm({ ...form, owner_password: e.target.value })} /></Field>
            <Field label="City"><input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
            <div className="lhc-modal-actions">
              <button type="button" className="lhc-btn ghost" onClick={() => setShowCreate(false)}>Cancel</button>
              <button type="submit" className="lhc-btn primary" disabled={busy}>{busy ? "Creating…" : "Create dealer"}</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}

/* ───────────────────────── users ───────────────────────── */

function UsersPage() {
  const { user } = useAuth();
  const isSuper = ["SUPERADMIN", "ADMIN"].includes(user?.role);
  const [users, setUsers] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "SALES", dealer_id: "" });

  const load = useCallback(() => {
    axios.get(`${API}/admin/users`).then((r) => setUsers(r.data)).catch((e) => setErr(errText(e)));
    if (isSuper) axios.get(`${API}/admin/tenants`).then((r) => setTenants(r.data.filter((t) => t.type === "dealer"))).catch(() => {});
  }, [isSuper]);
  useEffect(load, [load]);

  const dealerName = (id) => tenants.find((t) => t.dealer_id === id || t.id === id)?.name || id || "—";

  const setStatus = async (u, status) => {
    setErr(""); setOk("");
    try { await axios.patch(`${API}/admin/users/${u.id}`, { status }); setOk(`${u.email} → ${status}`); load(); }
    catch (e) { setErr(errText(e)); }
  };
  const del = async (u) => {
    setErr(""); setOk("");
    try { await axios.delete(`${API}/admin/users/${u.id}`); setOk(`Deleted ${u.email}`); load(); }
    catch (e) { setErr(errText(e)); }
  };
  const create = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setOk("");
    try {
      const payload = { name: form.name, email: form.email, password: form.password, role: form.role };
      if (isSuper && form.dealer_id) payload.dealer_id = form.dealer_id;
      await axios.post(`${API}/admin/users`, payload);
      setOk(`Created ${form.email}`);
      setShowCreate(false);
      setForm({ name: "", email: "", password: "", role: "SALES", dealer_id: "" });
      load();
    } catch (e2) { setErr(errText(e2)); } finally { setBusy(false); }
  };

  const roleChoices = isSuper ? ROLE_OPTIONS : ["SALES", "DESIGNER", "STORE_ADMIN", "INSTALLER", "CATALOG_MANAGER"];

  return (
    <>
      <div className="lhc-admin-head">
        <h1>Users</h1>
        <p>{isSuper ? "All platform users. Create manually — there is no invite system." : "Users in your store. Create manually within your dealer."}</p>
      </div>
      {err && <div className="lhc-admin-msg err">{err}</div>}
      {ok && <div className="lhc-admin-msg ok">{ok}</div>}
      <div className="lhc-panel">
        <div className="lhc-panel-head">
          <div><h2>Accounts</h2><div className="sub">{users ? `${users.length} users` : ""}</div></div>
          <button className="lhc-btn primary" data-testid="new-user-btn" onClick={() => setShowCreate(true)}>+ New user</button>
        </div>
        {!users ? <div className="lhc-loading">Loading…</div> : (
          <table className="lhc-table">
            <thead><tr><th>Name</th><th>Email</th><th>Role</th>{isSuper && <th>Dealer</th>}<th>Status</th><th style={{ textAlign: "right" }}>Actions</th></tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.email}`}>
                  <td style={{ fontWeight: 600, color: "#fff" }}>{u.name}</td>
                  <td className="lhc-mono">{u.email}</td>
                  <td><span className="lhc-badge role">{u.role}</span></td>
                  {isSuper && <td style={{ color: "#9a9aa3", fontSize: 12 }}>{u.dealer_id ? dealerName(u.dealer_id) : (u.manufacturer_id ? "Manufacturer" : "Platform")}</td>}
                  <td><StatusBadge status={u.status} /></td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    {u.id !== user?.id && (
                      <>
                        {u.status === "suspended"
                          ? <button className="lhc-btn ghost sm" onClick={() => setStatus(u, "active")}>Activate</button>
                          : <button className="lhc-btn ghost sm" onClick={() => setStatus(u, "suspended")}>Suspend</button>}
                        {" "}
                        <button className="lhc-btn danger sm" onClick={() => del(u)}>Delete</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <Modal title="Create user" hint="Manually provisioned — no invitation emails." onClose={() => setShowCreate(false)}>
          <form onSubmit={create}>
            <Field label="Full name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Email"><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Password"><input required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field>
            <Field label="Role">
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                {roleChoices.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            {isSuper && (
              <Field label="Dealer (optional — for store/dealer roles)">
                <select value={form.dealer_id} onChange={(e) => setForm({ ...form, dealer_id: e.target.value })}>
                  <option value="">— none / platform —</option>
                  {tenants.map((t) => <option key={t.id} value={t.dealer_id || t.id}>{t.name}</option>)}
                </select>
              </Field>
            )}
            <div className="lhc-modal-actions">
              <button type="button" className="lhc-btn ghost" onClick={() => setShowCreate(false)}>Cancel</button>
              <button type="submit" className="lhc-btn primary" disabled={busy}>{busy ? "Creating…" : "Create user"}</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}

/* ───────────────────────── feature flags ───────────────────────── */

function FlagsPage() {
  const [flags, setFlags] = useState(null);
  const [err, setErr] = useState("");
  const [savingKey, setSavingKey] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/admin/feature-flags`).then((r) => setFlags(r.data)).catch((e) => setErr(errText(e)));
  }, []);
  useEffect(load, [load]);

  const toggle = async (f, enabled) => {
    setSavingKey(f.key); setErr("");
    try { await axios.put(`${API}/admin/feature-flags/${f.key}`, { enabled }); load(); }
    catch (e) { setErr(errText(e)); } finally { setSavingKey(null); }
  };

  return (
    <>
      <div className="lhc-admin-head">
        <h1>Feature flags</h1>
        <p>Toggling a flag off removes that feature from every tenant's session and hides its pages.</p>
      </div>
      {err && <div className="lhc-admin-msg err">{err}</div>}
      <div className="lhc-panel">
        <div className="lhc-panel-head"><h2>Platform features</h2></div>
        {!flags ? <div className="lhc-loading">Loading…</div> : (
          <table className="lhc-table">
            <thead><tr><th>Feature</th><th>Key</th><th>Description</th><th style={{ textAlign: "right" }}>Enabled</th></tr></thead>
            <tbody>
              {flags.map((f) => (
                <tr key={f.key} data-testid={`flag-row-${f.key}`}>
                  <td style={{ fontWeight: 600, color: "#fff" }}>{f.label}</td>
                  <td className="lhc-mono" style={{ color: "#8a8a93" }}>{f.key}</td>
                  <td style={{ color: "#9a9aa3", fontSize: 12.5 }}>{f.description}</td>
                  <td style={{ textAlign: "right" }}>
                    <div style={{ display: "inline-flex", justifyContent: "flex-end" }}>
                      <Switch checked={!!f.enabled} disabled={savingKey === f.key} onChange={(v) => toggle(f, v)} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

/* ───────────────────────── audit ───────────────────────── */

function AuditPage() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    axios.get(`${API}/admin/audit?limit=200`).then((r) => setRows(r.data)).catch((e) => setErr(errText(e)));
  }, []);
  return (
    <>
      <div className="lhc-admin-head">
        <h1>Audit log</h1>
        <p>Generated by real actions (logins, user/tenant/flag changes, orders).</p>
      </div>
      {err && <div className="lhc-admin-msg err">{err}</div>}
      <div className="lhc-panel">
        <div className="lhc-panel-head"><h2>Recent events</h2><div className="sub">{rows ? `${rows.length} events` : ""}</div></div>
        {!rows ? <div className="lhc-loading">Loading…</div> : rows.length === 0 ? <div className="lhc-empty">No audit events yet.</div> : (
          <table className="lhc-table">
            <thead><tr><th>When</th><th>Action</th><th>Actor</th><th>Detail</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="lhc-mono" style={{ color: "#8a8a93", whiteSpace: "nowrap" }}>{(r.created_at || "").replace("T", " ").slice(0, 19)}</td>
                  <td><span className="lhc-badge role">{r.action}</span></td>
                  <td className="lhc-mono" style={{ fontSize: 12 }}>{r.actor_email || "—"}</td>
                  <td style={{ color: "#c7c7cf", fontSize: 12.5 }}>{typeof r.detail === "string" ? r.detail : JSON.stringify(r.detail)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

/* ───────────────────────── catalog ─────────────────────────
   The catalog manager's real workspace now lives at /catalog
   (frontend/src/catalog/CatalogTechApp.jsx). The old import-only
   CatalogPage that used to render at /admin/catalog was removed;
   /admin/catalog now redirects there. */

/* ───────────────────────── legacy tools (iframe of the prototype) ───────────────────────── */

function LegacyTools() {
  return (
    <>
      <div className="lhc-admin-head">
        <h1>Configurator workspace (prototype)</h1>
        <p>The original prototype admin tools, pending conversion in later stages. Catalog import, pricing and rules live here for now.</p>
      </div>
      <AdminWorkspace embedded />
    </>
  );
}

/* ───────────────────────── shell ───────────────────────── */

const NAV = [
  { to: "/admin", label: "Dashboard", end: true },
  { to: "/admin/tenants", label: "Tenants" },
  { to: "/admin/users", label: "Users" },
  { to: "/catalog", label: "Catalog", flag: "catalog_import" },
  { to: "/admin/flags", label: "Feature flags" },
  { to: "/admin/audit", label: "Audit log" },
  { to: "/admin/legacy", label: "Classic tools" },
];

export default function AdminApp() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const brand = user?.branding_context?.company_name || "LHC Platform";
  const flags = user?.feature_flags || {};
  // A nav item bound to a feature flag disappears when the Superadmin turns it off.
  const navItems = NAV.filter((n) => !n.flag || flags[n.flag] !== false);

  const onSignout = async () => { await logout(); navigate("/login", { replace: true }); };

  return (
    <div className="lhc-admin" data-testid="admin-app">
      <aside className="lhc-admin-side">
        <div className="lhc-admin-brand">
          <div className="lhc-admin-brand-mark">LH</div>
          <div>
            <div className="lhc-admin-brand-name">{brand}</div>
            <div className="lhc-admin-brand-sub">Superadmin</div>
          </div>
        </div>
        <nav className="lhc-admin-nav">
          <div className="lhc-admin-nav-label">Control</div>
          {navItems.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.label.split(" ")[0].toLowerCase()}`}
              className={({ isActive }) => (isActive ? "active" : "")}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="lhc-admin-side-foot">
          <div className="lhc-admin-user">
            <div className="lhc-admin-avatar">{initials(user?.name)}</div>
            <div className="lhc-admin-user-meta">
              <div className="lhc-admin-user-name">{user?.name}</div>
              <div className="lhc-admin-user-mail">{user?.email}</div>
            </div>
          </div>
          <button className="lhc-admin-signout" data-testid="admin-signout" onClick={onSignout}>Sign out</button>
        </div>
      </aside>
      <main className="lhc-admin-main">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="tenants" element={<TenantsPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="catalog" element={<Navigate to="/catalog" replace />} />
          <Route path="flags" element={<FlagsPage />} />
          <Route path="audit" element={<AuditPage />} />
          <Route path="legacy" element={<LegacyTools />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </main>
    </div>
  );
}
